/**
 * `clawnify ai-files install` — make a project agent-ready in one step.
 *
 * Mirrors the `npx convex ai-files install` pattern: writes Clawnify's
 * always-on rules block into each detected agent's rules file, installs
 * the bundled Clawnify skills, and wires the Clawnify MCP server into a
 * project `.mcp.json`. Re-running after a CLI upgrade refreshes all three
 * in place. `ai-files uninstall` strips them back out.
 */

import { SKILLS, CLI_VERSION } from "../lib/skills/registry.js";
import {
  agentByName,
  detectAgents,
  ALL_AGENTS,
  type Agent,
  type DetectedAgent,
} from "../lib/skills/detect.js";
import { installSkill, removeSkill } from "../lib/skills/install.js";
import { writeRules, removeRules, RULES_FILE } from "../lib/ai-files/rules.js";
import { writeMcpConfig, removeMcpConfig } from "../lib/ai-files/mcp.js";

interface InstallOptions {
  agent?: string;
  mcp?: boolean; // commander sets this false for --no-mcp
}

interface UninstallOptions {
  agent?: string;
}

/**
 * Resolve which agents to target. Explicit `--agent` wins; otherwise we
 * detect agents in the directory; if none are present we default to
 * Claude (writing CLAUDE.md) so a freshly-scaffolded app still becomes
 * agent-ready — matching Convex's CLAUDE.md-centric default.
 */
function resolveAgents(cwd: string, forced: string | undefined): { agents: DetectedAgent[]; defaulted: boolean } {
  if (forced) {
    if (!ALL_AGENTS.includes(forced as Agent)) {
      console.error(`Unknown agent: ${forced}\nValid: ${ALL_AGENTS.join(", ")}`);
      process.exit(1);
    }
    return { agents: [agentByName(forced as Agent)], defaulted: false };
  }
  const detected = detectAgents(cwd);
  if (detected.length > 0) return { agents: detected, defaulted: false };
  return { agents: [agentByName("claude")], defaulted: true };
}

interface InstallResult {
  rules: number;
  skills: number;
  mcp: boolean;
}

/**
 * Core install logic, shared by the command and by `clawnify init`'s
 * post-scaffold hook. Writes rules + skills + (optionally) MCP config,
 * logging each touched path. Returns a small tally.
 */
export function installAiFiles(
  cwd: string,
  agents: DetectedAgent[],
  opts: { mcp: boolean },
): InstallResult {
  // Rules — dedupe by target file (codex + openclaw both use AGENTS.md).
  const rulesFiles = [...new Set(agents.map((a) => RULES_FILE[a.agent]))];
  let rules = 0;
  for (const file of rulesFiles) {
    const r = writeRules(cwd, file);
    if (r.status === "skipped") {
      console.log(`  · rules → ${r.file}  (${r.reason})`);
    } else if (r.status === "updated") {
      console.log(`  ↑ rules → ${r.file}  (v${r.previousVersion} → v${CLI_VERSION})`);
      rules++;
    } else {
      console.log(`  ✓ rules → ${r.file}`);
      rules++;
    }
  }

  // Skills — install every bundled skill into every target agent.
  let skills = 0;
  for (const skill of SKILLS) {
    for (const agent of agents) {
      const s = installSkill(skill, agent, cwd);
      if (s.status === "skipped") {
        console.log(`  · skill ${skill.name} → ${s.path}  (${s.reason})`);
      } else if (s.status === "updated") {
        console.log(`  ↑ skill ${skill.name} → ${s.path}  (v${s.previousVersion} → v${CLI_VERSION})`);
        skills++;
      } else {
        console.log(`  ✓ skill ${skill.name} → ${s.path}`);
        skills++;
      }
    }
  }

  // MCP server config.
  let mcp = false;
  if (opts.mcp) {
    const m = writeMcpConfig(cwd);
    if (m.status === "skipped") {
      console.log(`  · mcp → ${m.file}  (${m.reason})`);
    } else {
      console.log(`  ${m.status === "updated" ? "↑" : "✓"} mcp → ${m.file}`);
      mcp = true;
    }
  }

  return { rules, skills, mcp };
}

export async function aiFilesInstallCommand(opts: InstallOptions): Promise<void> {
  const cwd = process.cwd();
  const { agents, defaulted } = resolveAgents(cwd, opts.agent);

  console.log(`Installing Clawnify AI files (CLI v${CLI_VERSION}):`);
  if (defaulted) {
    console.log("  No coding agent detected — defaulting to Claude (CLAUDE.md).");
  }
  console.log(`  Agents: ${agents.map((a) => a.agent).join(", ")}\n`);

  installAiFiles(cwd, agents, { mcp: opts.mcp !== false });

  console.log("\nDone. Your project is agent-ready.");
  console.log("Tip: leave `clawnify dev` running so generated types stay fresh.");
}

export async function aiFilesUninstallCommand(opts: UninstallOptions): Promise<void> {
  const cwd = process.cwd();
  // For uninstall, only touch what's actually present — detect, plus the
  // explicit override. Don't default to claude (nothing to guess at).
  const agents = opts.agent
    ? resolveAgents(cwd, opts.agent).agents
    : detectAgents(cwd);

  let removed = 0;
  const rulesFiles = [...new Set(agents.map((a) => RULES_FILE[a.agent]))];
  for (const file of rulesFiles) {
    if (removeRules(cwd, file)) {
      console.log(`  ✗ rules removed from ${file}`);
      removed++;
    }
  }
  for (const skill of SKILLS) {
    for (const agent of agents) {
      if (removeSkill(skill.name, agent, cwd)) {
        console.log(`  ✗ skill ${skill.name} removed from ${agent.agent}`);
        removed++;
      }
    }
  }
  if (removeMcpConfig(cwd)) {
    console.log("  ✗ clawnify server removed from .mcp.json");
    removed++;
  }

  console.log(`\nDone — removed ${removed} item${removed === 1 ? "" : "s"}.`);
}
